import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { toast } from 'sonner'
import { useLockedFieldHint } from '../../contexts/LockedFieldHintContext'

interface SingleSelectProps {
  value: string
  options: string[]
  onChange: (value: string) => void
  /** If this returns a non-empty string, that value is passed to `onChange` instead of the modal input. */
  onCreateOption?: (value: string) => void | string | Promise<void | string>
  /**
   * When set, the Add footer calls this instead of opening the built-in
   * create-name modal (use for full forms e.g. Add Team Member).
   */
  onAddClick?: () => void
  placeholder?: string
  addLabel?: string
  className?: string
  inputClassName?: string
  interactionLocked?: boolean
  lockedHint?: string
  /** Called when the user clicks the trigger while interactionLocked is true. */
  onLockedInteraction?: () => void
  /** Display string for each option in the dropdown list. Defaults to the option string. */
  getOptionLabel?: (option: string) => string
  /** Closed trigger label only; defaults to getOptionLabel / option string. */
  getTriggerLabel?: (option: string) => string
  searchPlaceholder?: string
  createModalTitle?: string
  createFieldLabel?: string
  createInputPlaceholder?: string
  /** Render the dropdown in a body portal (escapes overflow clipping containers). */
  dropdownPortal?: boolean
}

export interface SingleSelectHandle {
  /** Programmatically open the dropdown (no-op while interactionLocked). */
  open: () => void
  /** Focus the trigger button. */
  focus: () => void
}

const SingleSelect = forwardRef<SingleSelectHandle, SingleSelectProps>(function SingleSelect({
  value,
  options,
  onChange,
  onCreateOption,
  onAddClick,
  placeholder = 'Select...',
  addLabel = 'Add option',
  className,
  inputClassName,
  interactionLocked,
  lockedHint,
  onLockedInteraction,
  getOptionLabel: getOptionLabelProp,
  getTriggerLabel: getTriggerLabelProp,
  searchPlaceholder = 'Search...',
  createModalTitle,
  createFieldLabel = 'Name',
  createInputPlaceholder = 'Enter name',
  dropdownPortal = true
}, ref) {
  const radioGroupName = useId()
  const { showAtAnchor } = useLockedFieldHint()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [modalError, setModalError] = useState<string | null>(null)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const modalInputRef = useRef<HTMLInputElement | null>(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 })

  const modalTitle = createModalTitle ?? addLabel
  const labelFor = getOptionLabelProp ?? ((o: string) => o)
  const triggerLabelFor = getTriggerLabelProp ?? labelFor

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        if (interactionLocked) return
        setOpen(true)
      },
      focus: () => {
        triggerRef.current?.focus()
      }
    }),
    [interactionLocked]
  )

  const updateMenuPos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    // Match max-h-64 (+ search header + padding). Prefer below; flip above near viewport bottom.
    const estimatedMenuHeight = 280
    const gap = 4
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const openAbove =
      spaceBelow < Math.min(estimatedMenuHeight, 160) && spaceAbove > spaceBelow
    const measured =
      dropdownRef.current?.getBoundingClientRect().height ?? estimatedMenuHeight
    const top = openAbove
      ? Math.max(gap, rect.top - measured - gap)
      : rect.bottom + gap
    const width = Math.max(rect.width, 160)
    const maxLeft = Math.max(gap, window.innerWidth - width - gap)
    const left = Math.min(Math.max(gap, rect.left), maxLeft)
    setMenuPos({ top, left, width })
  }, [])

  useEffect(() => {
    if (open && dropdownPortal) updateMenuPos()
  }, [open, dropdownPortal, updateMenuPos])

  useEffect(() => {
    if (!open || !dropdownPortal) return
    const handleReposition = () => updateMenuPos()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [open, dropdownPortal, updateMenuPos])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!createModalOpen) return
    const t = window.setTimeout(() => modalInputRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [createModalOpen])

  useEffect(() => {
    if (!createModalOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createSubmitting) {
        setCreateModalOpen(false)
        setNewItemName('')
        setModalError(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [createModalOpen, createSubmitting])

  const filteredOptions = useMemo(() => {
    const lf = getOptionLabelProp ?? ((o: string) => o)
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((option) => {
      const label = lf(option).toLowerCase()
      return label.includes(q) || option.toLowerCase().includes(q)
    })
  }, [options, query, getOptionLabelProp])

  const displayValue = value ? triggerLabelFor(value) : placeholder

  const openCreateModal = () => {
    if (interactionLocked) return
    if (onAddClick) {
      setOpen(false)
      onAddClick()
      return
    }
    if (!onCreateOption) return
    setOpen(false)
    setNewItemName('')
    setModalError(null)
    setCreateModalOpen(true)
  }

  const closeCreateModal = () => {
    if (createSubmitting) return
    setCreateModalOpen(false)
    setNewItemName('')
    setModalError(null)
  }

  const handleModalSubmit = async () => {
    if (!onCreateOption) return
    const trimmed = newItemName.trim()
    if (!trimmed) {
      setModalError(`Please enter ${createFieldLabel.toLowerCase()}.`)
      return
    }
    if (options.some((option) => option.toLowerCase() === trimmed.toLowerCase())) {
      setModalError('This value already exists in the list.')
      return
    }
    setModalError(null)
    setCreateSubmitting(true)
    try {
      const created = await Promise.resolve(onCreateOption(trimmed))
      const next =
        typeof created === 'string' && created.trim() !== '' ? created.trim() : trimmed
      onChange(next)
      setCreateModalOpen(false)
      setNewItemName('')
      setQuery('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      toast.error(message)
    } finally {
      setCreateSubmitting(false)
    }
  }

  const createModal =
    createModalOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="singleselect-create-title"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !createSubmitting) closeCreateModal()
        }}
      >
        <div
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id="singleselect-create-title" className="text-lg font-semibold text-gray-900">
              {modalTitle}
            </h2>
            <button
              type="button"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              onClick={closeCreateModal}
              disabled={createSubmitting}
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <label className="mt-4 block text-sm font-semibold text-gray-900">{createFieldLabel}</label>
          <input
            ref={modalInputRef}
            className={clsx('input-primary mt-2', modalError && 'border-error-500 focus:border-error-500 focus:ring-error-500')}
            placeholder={createInputPlaceholder}
            value={newItemName}
            disabled={createSubmitting}
            onChange={(e) => {
              setNewItemName(e.target.value)
              if (modalError) setModalError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleModalSubmit()
              }
            }}
          />
          {modalError && <p className="mt-2 text-xs text-error-600">{modalError}</p>}
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={closeCreateModal} disabled={createSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary min-w-[7rem] justify-center"
              onClick={() => void handleModalSubmit()}
              disabled={createSubmitting}
            >
              {createSubmitting ? 'Adding…' : addLabel}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )

  const selectOption = (option: string) => {
    onChange(option)
    setQuery('')
    setOpen(false)
  }

  const dropdownMenu = (
    <div
      ref={dropdownPortal ? dropdownRef : undefined}
      className={clsx(
        'flex max-h-64 flex-col rounded-md border border-gray-200 bg-white shadow-lg',
        dropdownPortal ? 'fixed z-[10050]' : 'absolute top-full z-40 mt-1 w-full'
      )}
      style={
        dropdownPortal
          ? { top: menuPos.top, left: menuPos.left, width: menuPos.width }
          : undefined
      }
    >
      <div className="shrink-0 border-b border-gray-100 px-3 py-2">
        <input
          className="input-primary"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        {filteredOptions.length === 0 && (
          <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
        )}
        {filteredOptions.map((option) => {
          const selected = option === value
          const rowLabel = labelFor(option)
          return (
            <label
              key={option}
              className={clsx(
                'flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50',
                selected ? 'font-semibold text-primary-700' : 'text-gray-700'
              )}
            >
              <input
                type="radio"
                name={radioGroupName}
                checked={selected}
                onChange={() => selectOption(option)}
                className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="min-w-0 flex-1 whitespace-normal break-words text-left">{rowLabel}</span>
            </label>
          )
        })}
      </div>
      {(onAddClick != null || onCreateOption != null) && (
        <div className="shrink-0 border-t border-gray-100 bg-white px-3 py-2">
          <button
            type="button"
            className={clsx(
              'flex w-full items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700',
              interactionLocked && 'cursor-not-allowed opacity-50'
            )}
            onClick={(e) => {
              if (interactionLocked) {
                if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
                return
              }
              openCreateModal()
            }}
          >
            <PlusIcon className="h-4 w-4" />
            <span>{addLabel}</span>
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-disabled={interactionLocked || undefined}
        className={clsx(
          'input-primary flex w-full items-center justify-between gap-3 text-left',
          inputClassName,
          interactionLocked && 'cursor-not-allowed bg-page-bg text-heritage-grey opacity-70'
        )}
        onClick={(e) => {
          if (interactionLocked) {
            if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
            onLockedInteraction?.()
            return
          }
          setOpen((prev) => !prev)
        }}
      >
        <span
          className={clsx(
            'min-w-0 flex-1 whitespace-normal break-words text-left text-sm',
            interactionLocked
              ? 'text-heritage-grey'
              : value
                ? 'text-gray-900'
                : 'text-gray-400'
          )}
        >
          {displayValue}
        </span>
        <ChevronDownIcon
          className={clsx(
            'h-4 w-4 transition-transform',
            interactionLocked ? 'text-heritage-grey/70' : 'text-gray-400',
            open && !interactionLocked && 'rotate-180'
          )}
        />
      </button>

      {open && !dropdownPortal ? dropdownMenu : null}
      {open && dropdownPortal ? createPortal(dropdownMenu, document.body) : null}

      {createModal}
    </div>
  )
})

export default SingleSelect
